/**
 * Doserne — Outlook-style webmail (admin), backed by the IMAP/SMTP integration.
 * Real folders, paginated message list, HTML reading pane, compose/reply (plain
 * text) with a copy saved to Sent, delete→Trash, and a "Reply with Jarvis"
 * button that drafts the response for the admin to review and send.
 */
import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Inbox, Send, FileEdit, Trash2, ShieldAlert, Archive, Folder, Mail,
  RefreshCw, Search, X, Reply, ReplyAll, Forward, Sparkles, MailOpen, Plus, Paperclip, ChevronLeft, Link2,
  ChevronDown, ChevronRight, MessagesSquare,
  Bold, Italic, Underline, List, ListOrdered, RemoveFormatting,
  Palette, Type, PenLine, Trash, Image as ImageIcon, Users, Phone, Briefcase, MessageSquare,
} from "lucide-react";
import { useDoserneAuth } from "./doserneAuth";
import { useLang } from "./i18n";
import DoserneShell from "./DoserneShell";
import WeekCalendar from "./WeekCalendar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DICT = {
  ro: { title: "Email", compose: "Scrie email", search: "Caută în emailuri…", empty: "Niciun email aici.",
    loadmore: "Încarcă mai multe", to: "Către", subject: "Subiect", body: "Mesaj", send: "Trimite",
    savedraft: "Salvează ciornă", reply: "Răspunde", replyall: "Răspunde tuturor", jarvis: "Răspunde cu Jarvis", del: "Șterge",
    markunread: "Marchează necitit", forward: "Redirecționează", sending: "Se trimite…", sent_ok: "Email trimis ✓", draft_ok: "Ciornă salvată ✓",
    del_ok: "Mutat în coș ✓", pick: "Selectează un email ca să-l citești", cc: "Cc", noconn: "Emailul nu e conectat. Conectează-l din panoul Jarvis (iconița Mail).",
    jarvis_busy: "Jarvis scrie…", refresh: "Reîmprospătează", attach: "atașamente",
    p_today: "Azi", p_yesterday: "Ieri", p_this_week: "Săptămâna aceasta", p_last_week: "Săptămâna trecută", p_this_month: "Luna aceasta", p_older: "Mai vechi", msgs: "mesaje", f_all: "Tot", f_name: "Nume · email", f_files: "Cu fișiere", f_date: "Dată", f_apply: "Aplică", f_unread: "Necitite" },
  no: { title: "E-post", compose: "Skriv e-post", search: "Søk i e-post…", empty: "Ingen e-post her.",
    loadmore: "Last inn mer", to: "Til", subject: "Emne", body: "Melding", send: "Send",
    savedraft: "Lagre utkast", reply: "Svar", replyall: "Svar alle", jarvis: "Svar med Jarvis", del: "Slett",
    markunread: "Merk som ulest", forward: "Videresend", sending: "Sender…", sent_ok: "E-post sendt ✓", draft_ok: "Utkast lagret ✓",
    del_ok: "Flyttet til papirkurv ✓", pick: "Velg en e-post for å lese", cc: "Kopi", noconn: "E-post er ikke tilkoblet. Koble til fra Jarvis-panelet (Mail-ikonet).",
    jarvis_busy: "Jarvis skriver…", refresh: "Oppdater", attach: "vedlegg",
    p_today: "I dag", p_yesterday: "I går", p_this_week: "Denne uken", p_last_week: "Forrige uke", p_this_month: "Denne måneden", p_older: "Eldre", msgs: "meldinger", f_all: "Alt", f_name: "Navn · e-post", f_files: "Med vedlegg", f_date: "Dato", f_apply: "Bruk", f_unread: "Uleste" },
  en: { title: "Email", compose: "Compose", search: "Search email…", empty: "No email here.",
    loadmore: "Load more", to: "To", subject: "Subject", body: "Message", send: "Send",
    savedraft: "Save draft", reply: "Reply", replyall: "Reply all", jarvis: "Reply with Jarvis", del: "Delete",
    markunread: "Mark unread", forward: "Forward", sending: "Sending…", sent_ok: "Email sent ✓", draft_ok: "Draft saved ✓",
    del_ok: "Moved to trash ✓", pick: "Select an email to read", cc: "Cc", noconn: "Email not connected. Connect it from the Jarvis panel (Mail icon).",
    jarvis_busy: "Jarvis is writing…", refresh: "Refresh", attach: "attachments",
    p_today: "Today", p_yesterday: "Yesterday", p_this_week: "This week", p_last_week: "Last week", p_this_month: "This month", p_older: "Older", msgs: "messages", f_all: "All", f_name: "Name · email", f_files: "With files", f_date: "Date", f_apply: "Apply", f_unread: "Unread" },
};

const ROLE_ICON = { inbox: Inbox, sent: Send, drafts: FileEdit, junk: ShieldAlert, trash: Trash2, archive: Archive };

function fmtDate(d) {
  if (!d) return "";
  const dt = new Date(d);
  if (isNaN(dt)) return d;
  const now = new Date();
  const sameDay = dt.toDateString() === now.toDateString();
  return sameDay ? dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : dt.toLocaleDateString([], { day: "2-digit", month: "short" });
}

const isImageAtt = (att) => (att?.content_type || "").toLowerCase().startsWith("image/");

// Normalize a subject into a conversation/thread key (strip Re:/Sv:/Fwd:… prefixes).
function threadKey(subject) {
  let s = (subject || "").trim();
  const re = /^\s*(re|sv|svar|aw|fw|fwd|vs|res)\s*(\[\d+\])?\s*:\s*/i;
  let guard = 0;
  while (re.test(s) && guard < 12) { s = s.replace(re, ""); guard += 1; }
  return s.trim().toLowerCase() || "(no subject)";
}

// Which day/week bucket a date falls into (for the grouped list headers).
function periodKey(d) {
  const dt = new Date(d);
  if (isNaN(dt)) return "older";
  const now = new Date();
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dayMs = 86400000;
  const diffDays = Math.floor((startToday - new Date(dt.getFullYear(), dt.getMonth(), dt.getDate())) / dayMs);
  if (diffDays <= 0) return "today";
  if (diffDays === 1) return "yesterday";
  const dow = (startToday.getDay() + 6) % 7; // 0 = Monday
  if (diffDays <= dow) return "this_week";
  if (diffDays <= dow + 7) return "last_week";
  if (dt.getMonth() === now.getMonth() && dt.getFullYear() === now.getFullYear()) return "this_month";
  return "older";
}
const PERIOD_ORDER = ["today", "yesterday", "this_week", "last_week", "this_month", "older"];


// Inline thumbnail for image attachments — fetched with auth, shown directly
// in the reading pane; click zooms/opens full size.
function InlineImage({ folder, uid, att, authHeader, accountId, onOpen }) {
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let url = null;
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/doserne/jarvis/email/attachment`, {
          ...authHeader(), params: { folder, uid, index: att.index, account_id: accountId }, responseType: "blob",
        });
        url = URL.createObjectURL(res.data);
        if (alive) setSrc(url); else URL.revokeObjectURL(url);
      } catch { if (alive) setErr(true); }
    })();
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [folder, uid, att.index, authHeader, accountId]);
  if (err) return null;
  return (
    <button type="button" onClick={onOpen} data-testid="email-inline-image" title={att.filename}
      className="group relative rounded-xl overflow-hidden border border-slate-200 bg-slate-50 hover:border-cyan-400 transition">
      {src ? (
        <img src={src} alt={att.filename} loading="lazy"
          className="h-40 w-40 object-cover block group-hover:opacity-90" />
      ) : (
        <div className="h-40 w-40 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-300" /></div>
      )}
      <span className="absolute bottom-0 left-0 right-0 px-2 py-1 text-[10px] font-semibold text-white truncate bg-gradient-to-t from-black/70 to-transparent">{att.filename}</span>
    </button>
  );
}

const htmlEscape = (s) => (s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const textToHtml = (s) => htmlEscape(s).replace(/\n/g, "<br>");

// Compact rich-text editor with an Outlook-style formatting toolbar. Uncontrolled:
// initial HTML is set once on mount; changes are pushed up via onChange, and the
// parent can imperatively replace the content (e.g. a Jarvis draft) via the ref.
const RE_BTN = "w-8 h-8 flex items-center justify-center rounded hover:bg-slate-200 text-slate-600 transition-colors";
const TEXT_SWATCHES = ["#0f172a", "#ef4444", "#f97316", "#f59e0b", "#eab308", "#22c55e", "#14b8a6", "#0ea5e9", "#3b82f6", "#6366f1", "#a855f7", "#ec4899", "#64748b", "#ffffff"];
const HILITE_SWATCHES = ["#fef08a", "#bbf7d0", "#bfdbfe", "#fbcfe8", "#e9d5ff", "#fed7aa", "#fecaca"];
const RichEditor = React.forwardRef(function RichEditor({ initialHTML, onChange, signatures = [], onManageSignatures, onUploadImage }, ref) {
  const elRef = useRef(null);
  const imgInputRef = useRef(null);
  const [colorOpen, setColorOpen] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);
  useEffect(() => {
    if (elRef.current) elRef.current.innerHTML = initialHTML || "";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useImperativeHandle(ref, () => ({
    setHTML: (h) => { if (elRef.current) { elRef.current.innerHTML = h || ""; onChange?.(elRef.current.innerHTML); } },
    getHTML: () => elRef.current?.innerHTML || "",
    focus: () => elRef.current?.focus(),
  }));
  const exec = (cmd, val = null) => {
    elRef.current?.focus();
    try { document.execCommand(cmd, false, val); } catch { /* noop */ }
    onChange?.(elRef.current?.innerHTML || "");
  };
  const insertSignature = (html) => {
    elRef.current?.focus();
    try {
      const ok = document.execCommand("insertHTML", false, `<br>${html}<br>`);
      if (!ok && elRef.current) elRef.current.innerHTML += `<br>${html}<br>`;
    } catch { if (elRef.current) elRef.current.innerHTML += `<br>${html}<br>`; }
    onChange?.(elRef.current?.innerHTML || "");
  };
  const addLink = () => { const url = window.prompt("URL (https://…):", "https://"); if (url) exec("createLink", url); };
  const insertHTMLAtEnd = (html) => {
    elRef.current?.focus();
    try { if (!document.execCommand("insertHTML", false, html) && elRef.current) elRef.current.innerHTML += html; }
    catch { if (elRef.current) elRef.current.innerHTML += html; }
    onChange?.(elRef.current?.innerHTML || "");
  };
  const onPickImage = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) { window.alert("Imaginea e prea mare (max 3MB)."); return; }
    const style = "max-width:200px;height:auto;border-radius:4px";
    if (!onUploadImage) {
      const reader = new FileReader();
      reader.onload = () => insertHTMLAtEnd(`<img src="${reader.result}" alt="logo" style="${style}" />`);
      reader.readAsDataURL(f);
      return;
    }
    setUploadingImg(true);
    try {
      const url = await onUploadImage(f);
      insertHTMLAtEnd(`<img src="${url}" alt="logo" style="${style}" />`);
    } catch {
      window.alert("Image upload failed. Please try again.");
    }
    setUploadingImg(false);
  };
  const insertSocial = (label, emoji) => {
    const url = window.prompt(`Link ${label} (https://…):`, "https://");
    if (!url) return;
    insertHTMLAtEnd(`<a href="${url}" target="_blank" rel="noopener" style="color:#2563eb;text-decoration:none;font-weight:600">${emoji ? emoji + " " : ""}${label}</a>&nbsp;&nbsp;`);
  };
  const applyHilite = (color) => {
    elRef.current?.focus();
    try { document.execCommand("styleWithCSS", false, true); } catch { /* noop */ }
    try {
      if (!document.execCommand("hiliteColor", false, color)) document.execCommand("backColor", false, color);
    } catch { try { document.execCommand("backColor", false, color); } catch { /* noop */ } }
    onChange?.(elRef.current?.innerHTML || "");
  };
  const btn = (icon, cmd, tid, val) => (
    <button type="button" data-testid={tid} title={tid.replace("fmt-", "")}
      onMouseDown={(e) => { e.preventDefault(); exec(cmd, val); }} className={RE_BTN}>{icon}</button>
  );
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <div className="flex flex-wrap items-center gap-0.5 px-1.5 py-1 bg-slate-100 border-b border-slate-200" data-testid="email-format-toolbar">
        {btn(<Bold className="w-4 h-4" />, "bold", "fmt-bold")}
        {btn(<Italic className="w-4 h-4" />, "italic", "fmt-italic")}
        {btn(<Underline className="w-4 h-4" />, "underline", "fmt-underline")}
        <span className="w-px h-5 bg-slate-300 mx-1" />
        {/* text color — preset swatches */}
        <div className="relative">
          <button type="button" data-testid="fmt-color" title="color"
            onMouseDown={(e) => { e.preventDefault(); setColorOpen((o) => !o); }} className={RE_BTN}>
            <Palette className="w-4 h-4" />
          </button>
          {colorOpen && (
            <>
              <div className="fixed inset-0 z-[80]" onMouseDown={(e) => { e.preventDefault(); setColorOpen(false); }} />
              <div className="absolute top-9 left-0 z-[81] bg-white rounded-xl shadow-2xl border border-slate-200 p-3 w-56" data-testid="fmt-color-popover">
                <div className="text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1.5">Text color</div>
                <div className="grid grid-cols-7 gap-1.5 mb-2">
                  {TEXT_SWATCHES.map((c) => (
                    <button key={c} type="button" data-testid={`fmt-color-${c}`}
                      onMouseDown={(e) => { e.preventDefault(); exec("foreColor", c); setColorOpen(false); }}
                      className="w-6 h-6 rounded-full border border-slate-200 hover:scale-110 transition-transform"
                      style={{ background: c }} title={c} />
                  ))}
                </div>
                <div className="text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1.5">Highlight</div>
                <div className="grid grid-cols-7 gap-1.5">
                  {HILITE_SWATCHES.map((c) => (
                    <button key={c} type="button" data-testid={`fmt-hilite-${c}`}
                      onMouseDown={(e) => { e.preventDefault(); applyHilite(c); setColorOpen(false); }}
                      className="w-6 h-6 rounded-full border border-slate-200 hover:scale-110 transition-transform"
                      style={{ background: c }} title={c} />
                  ))}
                  <button type="button" data-testid="fmt-hilite-none"
                    onMouseDown={(e) => { e.preventDefault(); applyHilite("transparent"); setColorOpen(false); }}
                    className="w-6 h-6 rounded-full border border-slate-200 bg-white flex items-center justify-center hover:scale-110 transition-transform" title="None">
                    <X className="w-3 h-3 text-slate-400" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
        {/* font size */}
        <div className="flex items-center gap-1 pl-1">
          <Type className="w-4 h-4 text-slate-500" />
          <select data-testid="fmt-size" defaultValue="3"
            onMouseDown={(e) => e.stopPropagation()}
            onChange={(e) => { exec("fontSize", e.target.value); e.currentTarget.blur(); }}
            className="text-xs bg-white border border-slate-200 rounded px-1 py-0.5 focus:outline-none">
            <option value="2">S</option>
            <option value="3">M</option>
            <option value="5">L</option>
            <option value="6">XL</option>
          </select>
        </div>
        <span className="w-px h-5 bg-slate-300 mx-1" />
        {btn(<List className="w-4 h-4" />, "insertUnorderedList", "fmt-ul")}
        {btn(<ListOrdered className="w-4 h-4" />, "insertOrderedList", "fmt-ol")}
        <span className="w-px h-5 bg-slate-300 mx-1" />
        <button type="button" data-testid="fmt-link" title="link" onMouseDown={(e) => { e.preventDefault(); addLink(); }} className={RE_BTN}><Link2 className="w-4 h-4" /></button>
        {/* insert logo / image */}
        <button type="button" data-testid="fmt-image" title="image / logo" disabled={uploadingImg}
          onMouseDown={(e) => { e.preventDefault(); if (!uploadingImg) imgInputRef.current?.click(); }} className={RE_BTN}>
          {uploadingImg ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
        </button>
        <input ref={imgInputRef} type="file" accept="image/*" onChange={onPickImage} className="hidden" data-testid="fmt-image-input" />
        {/* quick social links */}
        <select data-testid="fmt-social" value=""
          onMouseDown={(e) => e.stopPropagation()}
          onChange={(e) => {
            const v = e.target.value;
            const map = { facebook: ["Facebook", "📘"], instagram: ["Instagram", "📸"], webshop: ["Webshop", "🛒"], market: ["Marketplace", "🏪"], website: ["Website", "🌐"], linkedin: ["LinkedIn", "💼"] };
            if (map[v]) insertSocial(map[v][0], map[v][1]);
            e.target.value = "";
          }}
          className="text-xs bg-white border border-slate-200 rounded px-1 py-0.5 focus:outline-none" title="Link social">
          <option value="">🔗 Social</option>
          <option value="facebook">Facebook</option>
          <option value="instagram">Instagram</option>
          <option value="webshop">Webshop</option>
          <option value="market">Marketplace</option>
          <option value="website">Website</option>
          <option value="linkedin">LinkedIn</option>
        </select>
        {btn(<RemoveFormatting className="w-4 h-4" />, "removeFormat", "fmt-clear")}
        <span className="w-px h-5 bg-slate-300 mx-1" />
        {/* signatures */}
        <div className="flex items-center gap-1">
          <PenLine className="w-4 h-4 text-slate-500" />
          <select data-testid="fmt-signature" value=""
            onMouseDown={(e) => e.stopPropagation()}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "__manage") onManageSignatures?.();
              else if (v) { const s = signatures.find((x) => x.id === v); if (s) insertSignature(s.html); }
              e.target.value = "";
            }}
            className="text-xs bg-white border border-slate-200 rounded px-1 py-0.5 focus:outline-none max-w-[130px]">
            <option value="">Signature</option>
            {signatures.map((s) => <option key={s.id} value={s.id}>{s.name}{s.is_default ? " ★" : ""}</option>)}
            <option value="__manage">⚙ Manage…</option>
          </select>
        </div>
      </div>
      <div ref={elRef} contentEditable suppressContentEditableWarning data-testid="email-compose-body"
        onInput={() => onChange?.(elRef.current?.innerHTML || "")}
        className="min-h-[180px] max-h-[40vh] overflow-y-auto dsc-scroll p-3 text-sm leading-relaxed focus:outline-none"
        style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }} />
    </div>
  );
});


// Normalize a prefilled recipient string so it ends with ", " → every address
// renders as a committed chip (not left editable in the trailing input).
const padRcpt = (s) => { const t = (s || "").trim().replace(/[,;\s]+$/, ""); return t ? `${t}, ` : ""; };

// Outlook-style recipient autocomplete + chips: committed recipients show as
// removable X tags; you keep typing the next one in the trailing input. Tab /
// Enter / comma / click commits. The underlying `value` stays a comma-separated
// string so the send/draft contract is unchanged.
function RecipientInput({ value, onChange, placeholder, testId, authHeader, accountId }) {
  const [sugs, setSugs] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [hi, setHi] = React.useState(0);
  const boxRef = React.useRef(null);
  const inputRef = React.useRef(null);
  const tRef = React.useRef(null);

  // Split the comma-separated value into committed chips + the trailing draft.
  const parts = (value || "").split(",");
  const chips = parts.slice(0, -1).map((s) => s.trim()).filter(Boolean);
  const draft = (parts[parts.length - 1] || "").replace(/^\s+/, "");

  const buildValue = (chipArr, draftStr) =>
    (chipArr.length ? chipArr.join(", ") + ", " : "") + draftStr;

  const fetchSugs = React.useCallback((token) => {
    if (tRef.current) clearTimeout(tRef.current);
    if (!token || token.length < 1) { setSugs([]); setOpen(false); return; }
    tRef.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/doserne/jarvis/email/contacts`, {
          ...authHeader(), params: { q: token, account_id: accountId },
        });
        const list = data.contacts || [];
        setSugs(list);
        setHi(0);
        setOpen(list.length > 0);
      } catch { setSugs([]); setOpen(false); }
    }, 180);
  }, [authHeader, accountId]);

  const handleChange = (e) => {
    let d = e.target.value;
    // Typing a comma commits the current draft as a chip.
    if (d.includes(",")) {
      const segs = d.split(",");
      const newChips = [...chips];
      for (let i = 0; i < segs.length - 1; i += 1) {
        const t = segs[i].trim();
        if (t) newChips.push(t);
      }
      d = segs[segs.length - 1].trimStart();
      onChange(buildValue(newChips, d));
      fetchSugs(d.trim());
      return;
    }
    onChange(buildValue(chips, d));
    fetchSugs(d.trim());
  };

  const accept = (c) => {
    onChange(buildValue([...chips, c.email], ""));
    setOpen(false);
    setSugs([]);
    inputRef.current?.focus();
  };

  const commitDraft = () => {
    const t = draft.trim();
    if (!t) return false;
    onChange(buildValue([...chips, t], ""));
    setOpen(false);
    setSugs([]);
    return true;
  };

  const removeChip = (idx) => {
    const next = chips.filter((_, i) => i !== idx);
    onChange(buildValue(next, draft));
    inputRef.current?.focus();
  };

  const onKeyDown = (e) => {
    if (open && sugs.length) {
      if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => (h + 1) % sugs.length); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => (h - 1 + sugs.length) % sugs.length); return; }
      if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); accept(sugs[hi]); return; }
      if (e.key === "Escape") { setOpen(false); return; }
    }
    if ((e.key === "Enter" || e.key === "Tab") && draft.trim()) { e.preventDefault(); commitDraft(); return; }
    // Backspace on empty draft removes the last chip.
    if (e.key === "Backspace" && !draft && chips.length) { e.preventDefault(); removeChip(chips.length - 1); }
  };

  React.useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  return (
    <div className="relative flex-1" ref={boxRef}>
      <div className="flex flex-wrap items-center gap-1" data-testid={`${testId}-chips`}
        onClick={() => inputRef.current?.focus()}>
        {chips.map((addr, i) => (
          <span key={`${addr}-${i}`} data-testid={`${testId}-chip-${i}`}
            className="inline-flex items-center gap-1 bg-cyan-50 border border-cyan-200 text-cyan-800 rounded-full pl-2.5 pr-1 py-0.5 text-xs font-medium max-w-full">
            <span className="truncate">{addr}</span>
            <button type="button" onClick={(e) => { e.stopPropagation(); removeChip(i); }}
              data-testid={`${testId}-chip-remove-${i}`} aria-label={`Remove ${addr}`}
              className="w-4 h-4 flex items-center justify-center rounded-full hover:bg-cyan-200 text-cyan-600 flex-shrink-0">
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <input ref={inputRef} value={draft} onChange={handleChange} onKeyDown={onKeyDown}
          onBlur={commitDraft}
          onFocus={() => { if (draft.trim()) fetchSugs(draft.trim()); }}
          data-testid={testId} autoComplete="off"
          className="flex-1 min-w-[120px] text-sm bg-transparent focus:outline-none py-0.5"
          placeholder={chips.length ? "" : placeholder} />
      </div>
      {open && sugs.length > 0 && (
        <div className="absolute left-0 right-0 top-full mt-1 z-40 bg-white rounded-xl border border-slate-200 shadow-xl overflow-hidden max-h-64 overflow-y-auto" data-testid="email-recipient-suggestions">
          {sugs.map((c, i) => {
            const initial = (c.name || c.email || "?").trim().charAt(0).toUpperCase();
            return (
              <button key={c.email} type="button"
                onMouseEnter={() => setHi(i)}
                onClick={() => accept(c)}
                data-testid={`email-recipient-sug-${i}`}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors ${i === hi ? "bg-cyan-50" : "hover:bg-slate-50"}`}>
                <span className="w-8 h-8 rounded-full bg-cyan-100 text-cyan-700 flex items-center justify-center text-sm font-bold flex-shrink-0">{initial}</span>
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-slate-800 truncate">{c.name || c.email}</span>
                  {c.name ? <span className="block text-xs text-slate-500 truncate">{c.email}</span> : null}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}


export default function DoserneEmail() {
  const { user, authHeader, loading: authLoading } = useDoserneAuth();
  const { lang } = useLang();
  const t = DICT[lang] || DICT.ro;

  const [folders, setFolders] = useState([]);
  const [active, setActive] = useState("inbox");
  const [list, setList] = useState({ messages: [], total: 0, has_more: false, page: 1 });
  const [loadingList, setLoadingList] = useState(false);
  const [search, setSearch] = useState("");
  const [scope, setScope] = useState("all"); // all | name | files | date
  const [unreadOnly, setUnreadOnly] = useState(false);
  // Email sync banner removed Jun 2026 — downloads happen silently in bg.
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selected, setSelected] = useState(null);
  const [loadingMsg, setLoadingMsg] = useState(false);
  const [notConnected, setNotConnected] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [activeAccount, setActiveAccount] = useState("");
  const [acctMenuOpen, setAcctMenuOpen] = useState(false);
  const [connectOpen, setConnectOpen] = useState(false);
  const [conn, setConn] = useState({ email: "", password: "", host: "", imap_port: 993, smtp_port: 465 });
  const [connecting, setConnecting] = useState(false);
  const [localDrafts, setLocalDrafts] = useState([]);
  const [compose, setCompose] = useState(null); // {mode,to,subject,body,cc,in_reply_to}
  const [sending, setSending] = useState(false);
  const [jarvisBusy, setJarvisBusy] = useState(false);
  const [myEmail, setMyEmail] = useState("");
  const [expanded, setExpanded] = useState({}); // thread key → bool (show all msgs)
  const editorRef = useRef(null);
  const loadingMoreRef = useRef(false);
  const listScrollRef = useRef(null);
  const [signatures, setSignatures] = useState([]);
  const [sigModal, setSigModal] = useState(false);
  const [sigEdit, setSigEdit] = useState(null); // {id?, name, html, is_default}
  const [dragOver, setDragOver] = useState(false);

  const loadSignatures = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/doserne/jarvis/email/signatures`, authHeader());
      setSignatures(data.signatures || []);
    } catch { /* ignore */ }
  }, [authHeader]);
  const uploadInlineImage = useCallback(async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const cfg = authHeader() || {};
    const { data } = await axios.post(`${API}/doserne/jarvis/email/signature-logo`, fd, { headers: { ...(cfg.headers || {}) } });
    return `${API}/doserne/jarvis/email/asset/${data.id}`;
  }, [authHeader]);
  useEffect(() => { loadSignatures(); }, [loadSignatures]);
  const defaultSigHtml = () => {
    const d = signatures.find((s) => s.is_default) || null;
    return d ? `<div data-sig="1">${d.html}</div>` : "";
  };
  const saveSignature = async () => {
    if (!sigEdit || !(sigEdit.name || "").trim()) { toast.error("Signature name?"); return; }
    try {
      await axios.post(`${API}/doserne/jarvis/email/signatures`, {
        id: sigEdit.id || "", name: sigEdit.name, html: sigEdit.html || "", is_default: !!sigEdit.is_default,
      }, authHeader());
      toast.success("Signature saved ✓");
      setSigEdit(null);
      loadSignatures();
    } catch { toast.error("Error saving"); }
  };
  const deleteSignature = async (id) => {
    try {
      await axios.delete(`${API}/doserne/jarvis/email/signatures/${id}`, authHeader());
      loadSignatures();
      if (sigEdit?.id === id) setSigEdit(null);
    } catch { toast.error("Error"); }
  };
  const fileRef = useRef(null);
  const jarvisSession = useRef(`mail-${Date.now()}`);

  const isAdmin = !!user?.is_admin;

  // Client catalog (built from doserne_projects via /email/clients).
  const [clientsView, setClientsView] = useState(false); // when true, right pane shows the client catalog
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [clientSearch, setClientSearch] = useState("");
  const [selectedClient, setSelectedClient] = useState(null); // { client, projects, threads }
  const [clientDetailLoading, setClientDetailLoading] = useState(false);
  const openClientDetail = useCallback(async (c) => {
    if (!c) return;
    setSelected(null); setCompose(null);
    setClientDetailLoading(true);
    try {
      const key = c.email || c.name;
      const { data } = await axios.get(`${API}/doserne/jarvis/email/client-detail`, { ...authHeader(), params: { key } });
      setSelectedClient({ ...data, _row: c });
    } catch { toast.error("Nu am putut încărca detaliile clientului."); setSelectedClient(null); }
    finally { setClientDetailLoading(false); }
  }, [API, authHeader]);
  // Per-project email threads — fetched on demand when a project card is expanded.
  const [projectThreads, setProjectThreads] = useState({}); // { [project_id]: { loading, threads } }
  const toggleProjectThreads = useCallback(async (projectId) => {
    setProjectThreads((prev) => {
      // Collapse if already loaded or loading.
      if (prev[projectId]) {
        const copy = { ...prev };
        delete copy[projectId];
        return copy;
      }
      return { ...prev, [projectId]: { loading: true, threads: [] } };
    });
    const already = projectThreads[projectId];
    if (already) return;
    try {
      const { data } = await axios.get(`${API}/doserne/jarvis/email/project-threads`, {
        ...authHeader(), params: { project_id: projectId },
      });
      setProjectThreads((prev) => ({ ...prev, [projectId]: { loading: false, threads: data?.threads || [] } }));
    } catch {
      setProjectThreads((prev) => ({ ...prev, [projectId]: { loading: false, threads: [] } }));
      toast.error("Nu am putut încărca conversațiile proiectului.");
    }
  }, [API, authHeader, projectThreads]);
  const loadClients = useCallback(async (q = "") => {
    setClientsLoading(true);
    try {
      const { data } = await axios.get(`${API}/doserne/jarvis/email/clients`, { ...authHeader(), params: { q } });
      setClients(data?.clients || []);
    } catch { setClients([]); }
    finally { setClientsLoading(false); }
  }, [API, authHeader]);
  useEffect(() => { if (isAdmin && clientsView) loadClients(clientSearch); }, [isAdmin, clientsView, loadClients]); // eslint-disable-line

  const loadAccounts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/doserne/jarvis/email/accounts`, authHeader());
      const accs = data.accounts || [];
      setAccounts(accs);
      if (accs.length) {
        setActiveAccount((prev) => (prev && accs.some((a) => a.id === prev)) ? prev : (accs.find((a) => a.is_default) || accs[0]).id);
        setNotConnected(false);
      } else {
        setActiveAccount("");
        setNotConnected(true);
      }
    } catch { setNotConnected(true); }
  }, [authHeader]);
  useEffect(() => { if (isAdmin) loadAccounts(); }, [isAdmin, loadAccounts]);

  useEffect(() => {
    if (!isAdmin || !activeAccount) return;
    const a = accounts.find((x) => x.id === activeAccount);
    setMyEmail((a?.email || "").toLowerCase());
  }, [isAdmin, activeAccount, accounts]);

  const loadFolders = useCallback(async () => {
    if (!activeAccount) return;
    try {
      const { data } = await axios.get(`${API}/doserne/jarvis/email/folders`, { ...authHeader(), params: { account_id: activeAccount } });
      setFolders(data.folders || []);
      setNotConnected(false);
    } catch (e) {
      if (e?.response?.status === 400) setNotConnected(true);
    }
  }, [authHeader, activeAccount]);

  const loadList = useCallback(async (folder, page = 1, q = "", opts = {}) => {
    if (!activeAccount) return;
    const sc = opts.scope ?? scope;
    const df = opts.dateFrom ?? dateFrom;
    const dt = opts.dateTo ?? dateTo;
    const uo = opts.unreadOnly ?? unreadOnly;
    const q2 = (q || "").trim();
    // Local index now stores full body text → fast, complete search offline.
    // "all"/"name" with a query use the index; empty query shows the plain inbox.
    const useIndex = (sc === "files" || sc === "date" || ((sc === "all" || sc === "name") && !!q2));
    setLoadingList(true);
    try {
      let data;
      if (useIndex) {
        const res = await axios.get(`${API}/doserne/jarvis/email/search`, {
          ...authHeader(), params: { folder, q: q || "", scope: sc, date_from: df, date_to: dt, page, per_page: 30, account_id: activeAccount, unread_only: uo },
        });
        data = res.data;
      } else {
        const res = await axios.get(`${API}/doserne/jarvis/email/messages`, {
          ...authHeader(), params: { folder, page, per_page: 25, search: q2, account_id: activeAccount, unread_only: uo },
        });
        data = res.data;
      }
      setList((prev) => ({
        ...data,
        messages: page > 1 ? [...prev.messages, ...data.messages] : data.messages,
      }));
      setNotConnected(false);
    } catch (e) {
      if (e?.response?.status === 400) setNotConnected(true);
    }
    setLoadingList(false);
  }, [authHeader, scope, dateFrom, dateTo, unreadOnly, activeAccount]);

  // Background: keep the whole mailbox index fresh across ALL accounts & folders.
  useEffect(() => {
    if (!isAdmin || !accounts.length) return;
    // Instant sweep first: enforce the rolling 1000-mail cap across all folders,
    // then kick off the background sync.
    axios.post(`${API}/doserne/jarvis/email/prune`, {}, authHeader())
      .catch(() => {})
      .finally(() => {
        axios.post(`${API}/doserne/jarvis/email/sync`, {}, { ...authHeader(), params: { folder: "ALL" } }).catch(() => {});
      });
  }, [isAdmin, accounts.length]); // eslint-disable-line

  // Sync progress polling removed with the UI banner (Jun 2026) — the
  // initial download runs silently in the background and the folder-list
  // auto-refresh below picks up new emails as they land.

  useEffect(() => { if (isAdmin && activeAccount) loadFolders(); }, [isAdmin, activeAccount, loadFolders]);
  useEffect(() => { if (isAdmin && activeAccount) { setSelected(null); loadList(active, 1, search); } }, [active, isAdmin, activeAccount]); // eslint-disable-line

  // Auto-refresh the folder list so new emails appear without a manual refresh.
  const refreshGuard = useRef({});
  useEffect(() => { refreshGuard.current = { search, compose, selected, active, activeAccount }; });
  useEffect(() => {
    if (!isAdmin) return undefined;
    const iv = setInterval(() => {
      const g = refreshGuard.current;
      if (g.activeAccount && !g.search && !g.compose && !g.selected) {
        loadList(g.active, 1, "");
      }
    }, 45000);
    const onNew = async () => {
      const g = refreshGuard.current;
      if (!g.activeAccount || g.compose) return;
      // The watcher detects the mail over IMAP but it isn't in the local index
      // yet — sync the current folder first, THEN reload so it actually appears
      // (works even while a message is open; the open message is untouched).
      try {
        await axios.post(`${API}/doserne/jarvis/email/sync`, {}, { ...authHeader(), params: { folder: g.active || "inbox" } });
      } catch { /* ignore */ }
      if (!g.search) loadList(g.active, 1, "");
    };
    window.addEventListener("doserne:new-email", onNew);
    return () => { clearInterval(iv); window.removeEventListener("doserne:new-email", onNew); };
  }, [isAdmin, loadList]);


  // Auto-fill: if the loaded list doesn't fill the pane yet (few results per page),
  // keep pulling the next page so the user can scroll through everything seamlessly.
  useEffect(() => {
    const el = listScrollRef.current;
    if (!el || !list.has_more || loadingList || loadingMoreRef.current) return;
    if (el.scrollHeight <= el.clientHeight + 40) {
      loadingMoreRef.current = true;
      Promise.resolve(loadList(active, list.page + 1, search)).finally(() => { loadingMoreRef.current = false; });
    }
  }, [list, loadingList, active, search, loadList]);


  const switchAccount = (id) => {
    if (id === activeAccount) { setAcctMenuOpen(false); return; }
    autoSaveDraftIfDirty(compose);
    setCompose(null);
    setSelected(null);
    setSearch("");
    setScope("all");
    setActive("inbox");
    setActiveAccount(id);
    setAcctMenuOpen(false);
  };

  const connectAccount = async () => {
    if (!conn.email.trim() || !conn.password) { toast.error("Email & password required"); return; }
    setConnecting(true);
    try {
      const { data } = await axios.post(`${API}/doserne/jarvis/email/connect`, {
        email: conn.email.trim(), password: conn.password, host: conn.host.trim() || null,
        imap_port: Number(conn.imap_port) || 993, smtp_port: Number(conn.smtp_port) || 465,
      }, authHeader());
      toast.success("Account connected ✓");
      setConnectOpen(false);
      setConn({ email: "", password: "", host: "", imap_port: 993, smtp_port: 465 });
      await loadAccounts();
      if (data.id) setActiveAccount(data.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Connection failed"); }
    setConnecting(false);
  };

  const disconnectAccount = async (id) => {
    if (!window.confirm("Remove this account from the app?")) return;
    try {
      await axios.post(`${API}/doserne/jarvis/email/disconnect`, {}, { ...authHeader(), params: { account_id: id } });
      toast.success("Account removed");
      if (id === activeAccount) { setSelected(null); setCompose(null); setActive("inbox"); }
      await loadAccounts();
    } catch { toast.error("Error"); }
  };

  const loadLocalDrafts = useCallback(async () => {
    if (!activeAccount) { setLocalDrafts([]); return; }
    try {
      const { data } = await axios.get(`${API}/doserne/jarvis/email/drafts-local`, { ...authHeader(), params: { account_id: activeAccount } });
      setLocalDrafts(data.drafts || []);
    } catch { setLocalDrafts([]); }
  }, [authHeader, activeAccount]);
  useEffect(() => { if (isAdmin && activeAccount && active === "drafts") loadLocalDrafts(); }, [isAdmin, activeAccount, active, loadLocalDrafts]);

  const openDraftItem = (d) => {
    setSelected(null);
    const c = {
      mode: d.in_reply_to ? "reply" : "new",
      to: padRcpt(d.to), cc: padRcpt(d.cc), subject: d.subject || "",
      body: d.body || "", in_reply_to: d.in_reply_to || "", ref: d.ref, replyMsg: null,
    };
    c._snap0 = snapOf(c);
    setCompose(c);
  };

  const deleteLocalDraft = async (d) => {
    try {
      await axios.delete(`${API}/doserne/jarvis/email/draft`, { ...authHeader(), params: { ref: d.ref, account_id: activeAccount } });
      loadLocalDrafts();
    } catch { toast.error("Error"); }
  };

  const openMessage = async (m) => {
    autoSaveDraftIfDirty(compose);
    setCompose(null);
    setLoadingMsg(true);
    setSelected({ ...m, loading: true });
    try {
      const { data } = await axios.get(`${API}/doserne/jarvis/email/message`, {
        ...authHeader(), params: { folder: active, uid: m.uid, account_id: activeAccount },
      });
      setSelected(data);
      if (m.unread) {
        setList((p) => ({ ...p, messages: p.messages.map((x) => x.uid === m.uid ? { ...x, unread: false } : x) }));
        setFolders((fs) => fs.map((f) => f.role === active || f.name === active ? { ...f, unread: Math.max(0, f.unread - 1) } : f));
      }
    } catch {
      toast.error("Error opening");
      setSelected(null);
    }
    setLoadingMsg(false);
  };

  // Jarvis voice command "open the Linderud email" → open a specific message by
  // folder+uid. Handles the race where the account list isn't loaded yet.
  const pendingOpenRef = useRef(null);
  const openByUid = useCallback(async (folder, uid) => {
    if (!uid) return;
    const f = folder || "inbox";
    if (!activeAccount) { pendingOpenRef.current = { folder: f, uid }; return; }
    setActive(f);
    setCompose(null);
    setLoadingMsg(true);
    setSelected({ uid, folder: f, loading: true });
    try {
      const { data } = await axios.get(`${API}/doserne/jarvis/email/message`, {
        ...authHeader(), params: { folder: f, uid, account_id: activeAccount },
      });
      setSelected(data);
    } catch {
      toast.error("Error opening");
      setSelected(null);
    }
    setLoadingMsg(false);
  }, [activeAccount, authHeader]);

  useEffect(() => {
    const onOpen = (e) => { const { folder, uid } = e.detail || {}; openByUid(folder, uid); };
    window.addEventListener("doserne:open-email", onOpen);
    return () => window.removeEventListener("doserne:open-email", onOpen);
  }, [openByUid]);

  useEffect(() => {
    if (activeAccount && pendingOpenRef.current) {
      const { folder, uid } = pendingOpenRef.current;
      pendingOpenRef.current = null;
      openByUid(folder, uid);
    }
  }, [activeAccount, openByUid]);


  const doDelete = async (m) => {
    try {
      await axios.post(`${API}/doserne/jarvis/email/delete`, { folder: active, uid: m.uid }, { ...authHeader(), params: { account_id: activeAccount } });
      setList((p) => ({ ...p, messages: p.messages.filter((x) => x.uid !== m.uid), total: p.total - 1 }));
      if (selected?.uid === m.uid) setSelected(null);
      toast.success(t.del_ok);
    } catch { toast.error("Error"); }
  };

  const markUnread = async (m) => {
    try {
      await axios.post(`${API}/doserne/jarvis/email/mark`, { folder: active, uid: m.uid, seen: false }, { ...authHeader(), params: { account_id: activeAccount } });
      setList((p) => ({ ...p, messages: p.messages.map((x) => x.uid === m.uid ? { ...x, unread: true } : x) }));
      toast.success("✓");
    } catch { toast.error("Error"); }
  };

  const buildQuote = (msg) => {
    const toStr = (msg.to || []).map((x) => x.addr).join(", ");
    const headerHtml = [
      `From: ${htmlEscape(msg.from)} &lt;${htmlEscape(msg.from_addr)}&gt;`,
      `Date: ${htmlEscape(msg.date || "")}`,
      `To: ${htmlEscape(toStr)}`,
      `Subject: ${htmlEscape(msg.subject || "")}`,
    ].join("<br>");
    // Keep the ORIGINAL rich body (inline images/logos are already embedded as
    // data: URIs, links preserved) — like Outlook. Fall back to plain text.
    const stripDoc = (h) => {
      let s = h || "";
      const b = s.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
      if (b) s = b[1];
      return s.replace(/<script[\s\S]*?<\/script>/gi, "").replace(/<style[\s\S]*?<\/style>/gi, "");
    };
    const bodyHtml = (msg.html && msg.html.trim())
      ? stripDoc(msg.html)
      : `<div style="color:#334155">${textToHtml((msg.text || "").replace(/\r/g, ""))}</div>`;
    // Underscore separator line between the reply and the quoted conversation.
    return `<div>________________________________</div>`
      + `<div style="color:#64748b;font-size:12px">${headerHtml}</div><br>`
      + `<blockquote style="margin:0 0 0 2px;padding-left:12px;border-left:2px solid #e2e8f0">${bodyHtml}</blockquote>`;
  };

  const snapOf = (c) => `${c.to || ""}\n${c.cc || ""}\n${c.subject || ""}\n${c.body || ""}`;
  const refForReply = (msg) => `reply:${msg.message_id || threadKey(msg.subject || "")}`;
  const fetchDraft = async (ref) => {
    try {
      const { data } = await axios.get(`${API}/doserne/jarvis/email/draft`, { ...authHeader(), params: { ref, account_id: activeAccount } });
      return data.draft || null;
    } catch { return null; }
  };

  const openCompose = async (mode, msg, all = false) => {
    if ((mode === "reply" || mode === "replyAll") && msg) {
      const ref = refForReply(msg);
      const saved = await fetchDraft(ref);
      const exclude = new Set([myEmail, (msg.from_addr || "").toLowerCase()].filter(Boolean));
      const ccAddrs = all
        ? [...(msg.to || []), ...(msg.cc || [])].map((x) => x.addr).filter((a) => a && !exclude.has(a.toLowerCase()))
        : [];
      const ccUniq = [...new Set(ccAddrs)];
      const c = {
        mode: all ? "replyAll" : "reply",
        to: padRcpt(saved ? saved.to : (msg.from_addr || "")),
        cc: padRcpt(saved ? saved.cc : ccUniq.join(", ")),
        subject: saved ? saved.subject : ((msg.subject || "").toLowerCase().startsWith("re:") ? msg.subject : `Re: ${msg.subject || ""}`),
        body: saved ? saved.body : `<p><br></p>${defaultSigHtml()}<p><br></p>${buildQuote(msg)}`,
        in_reply_to: msg.message_id || "",
        replyMsg: msg,
        ref,
      };
      c._snap0 = snapOf(c);
      setCompose(c);
      if (saved) toast.success("Draft restored");
    } else {
      setSelected(null);
      const ref = "new";
      const saved = await fetchDraft(ref);
      const c = {
        mode: "new",
        to: saved ? padRcpt(saved.to) : "", cc: saved ? padRcpt(saved.cc) : "",
        subject: saved ? saved.subject : "",
        body: saved ? saved.body : `<p><br></p>${defaultSigHtml()}`,
        in_reply_to: "", ref,
      };
      c._snap0 = snapOf(c);
      setCompose(c);
      if (saved) toast.success("Draft restored");
    }
  };

  const openComposeForClient = (c) => {
    if (!c?.email) { toast.error("Clientul nu are email în catalog."); return; }
    const name = (c.name || "").trim();
    const addr = name ? `${name} <${c.email}>` : c.email;
    setSelected(null); setClientsView(false);
    const comp = {
      mode: "new", to: padRcpt(addr), cc: "", subject: "",
      body: `<p><br></p>${defaultSigHtml()}`, in_reply_to: "", ref: "new",
    };
    comp._snap0 = snapOf(comp);
    setCompose(comp);
  };

  const forwardMessage = async (msg) => {
    if (!msg) return;
    const real = selected && selected.uid === msg.uid ? selected : msg;
    const origAtts = real.attachments || [];
    setSelected(null);
    const subj = (msg.subject || "").toLowerCase().startsWith("fwd:") ? msg.subject : `Fwd: ${msg.subject || ""}`;
    const c = {
      mode: "forward",
      to: "", cc: "", subject: subj,
      body: `<p><br></p>${defaultSigHtml()}<p><br></p>${buildQuote(msg)}`,
      in_reply_to: "", replyMsg: null, ref: `fwd:${msg.message_id || threadKey(msg.subject || "")}`,
      attachments: [],
    };
    c._snap0 = snapOf(c);
    setCompose(c);
    // Carry over the original files (fetched as base64), appended as they arrive.
    if (origAtts.length) {
      toast.loading(`Se atașează ${origAtts.length} fișier(e)…`, { id: "fwd-att" });
      let ok = 0;
      await Promise.allSettled(origAtts.map(async (att) => {
        const res = await axios.get(`${API}/doserne/jarvis/email/attachment`, {
          ...authHeader(), params: { folder: active, uid: msg.uid, index: att.index, account_id: activeAccount }, responseType: "blob",
        });
        const data = await new Promise((resolve) => { const r = new FileReader(); r.onload = () => resolve(r.result); r.readAsDataURL(res.data); });
        setCompose((cur) => cur && cur.mode === "forward" ? { ...cur, attachments: [...(cur.attachments || []), { filename: att.filename, content_type: att.content_type || "application/octet-stream", data }] } : cur);
        ok += 1;
      }));
      toast.success(`${ok}/${origAtts.length} fișier(e) atașate`, { id: "fwd-att" });
    }
  };

  const replyWithJarvis = async (msg) => {
    const m = msg || compose?.replyMsg;
    if (!compose || (compose.mode !== "reply" && compose.mode !== "replyAll")) openCompose("reply", m);
    setJarvisBusy(true);
    try {
      const context = (m.text || m.html || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").slice(0, 2500);
      const prompt = `Write a professional, polite reply draft to this email. Reply with the reply text ONLY (no explanations, no subject line, no "Here's the draft"). Email from ${m.from} (${m.from_addr}). Subject: ${m.subject}. Content:\n${context}`;
      const { data } = await axios.post(`${API}/doserne/jarvis/chat`,
        { session_id: jarvisSession.current, message: prompt }, authHeader());
      const draft = (data.reply || "").trim();
      // Jarvis draft (plain) → HTML, above the signature and the quoted original.
      const newHtml = `<div>${textToHtml(draft)}</div><p><br></p>${defaultSigHtml()}<p><br></p>${buildQuote(m)}`;
      if (editorRef.current) editorRef.current.setHTML(newHtml);
      setCompose((c) => c ? { ...c, body: newHtml } : c);
    } catch { toast.error("Jarvis couldn't write the draft"); }
    setJarvisBusy(false);
  };

  const sendEmail = async () => {
    const cleanAddrs = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean).join(", ");
    const toClean = cleanAddrs(compose?.to);
    const ccClean = cleanAddrs(compose?.cc);
    if (!toClean) { toast.error(t.to + "?"); return; }
    const ref = compose.ref;
    setSending(true);
    try {
      await axios.post(`${API}/doserne/jarvis/email/send`, {
        to: toClean, subject: compose.subject, body: compose.body, cc: ccClean, in_reply_to: compose.in_reply_to,
        attachments: compose.attachments || [], is_html: true,
      }, { ...authHeader(), params: { account_id: activeAccount } });
      toast.success(t.sent_ok);
      setCompose(null);
      // Sent → clear the resume-draft for this conversation.
      if (ref) axios.delete(`${API}/doserne/jarvis/email/draft`, { ...authHeader(), params: { ref, account_id: activeAccount } }).then(() => { loadFolders(); loadLocalDrafts(); }).catch(() => {});
      loadFolders();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error sending"); }
    setSending(false);
  };

  const saveDraft = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/doserne/jarvis/email/draft`, {
        to: compose.to, subject: compose.subject, body: compose.body, cc: compose.cc,
        ref: compose.ref, in_reply_to: compose.in_reply_to || "",
      }, { ...authHeader(), params: { account_id: activeAccount } });
      toast.success(t.draft_ok);
      setCompose(null);
      loadFolders();
      loadLocalDrafts();
    } catch { toast.error("Error"); }
    setSending(false);
  };

  // Auto-save the draft on exit ONLY if the user actually changed something
  // (so opening then closing an untouched reply won't spam drafts). One draft
  // per conversation (upsert by ref) → reopening the reply resumes it.
  const autoSaveDraftIfDirty = (c) => {
    if (!c || c._snap0 === undefined || snapOf(c) === c._snap0) return;
    axios.post(`${API}/doserne/jarvis/email/draft`,
      { to: c.to, subject: c.subject, body: c.body, cc: c.cc, ref: c.ref, in_reply_to: c.in_reply_to || "" },
      { ...authHeader(), params: { account_id: activeAccount } })
      .then(() => { toast.success(t.draft_ok); loadFolders(); loadLocalDrafts(); })
      .catch(() => {});
  };
  const closeCompose = () => { autoSaveDraftIfDirty(compose); setCompose(null); };
  // Warn before leaving the tab/refresh if there's an unsaved compose in progress.
  useEffect(() => {
    if (!compose || compose._snap0 === undefined) return undefined;
    const handler = (e) => { if (snapOf(compose) !== compose._snap0) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [compose]); // eslint-disable-line
  const grouped = useMemo(() => {
    const threads = new Map();
    for (const m of list.messages) {
      const k = threadKey(m.subject);
      if (!threads.has(k)) threads.set(k, { key: k, msgs: [], latest: m, unread: false });
      const th = threads.get(k);
      th.msgs.push(m);
      th.unread = th.unread || !!m.unread;
      if (new Date(m.date) > new Date(th.latest.date)) th.latest = m;
    }
    const buckets = new Map();
    for (const th of threads.values()) {
      const pk = periodKey(th.latest.date);
      if (!buckets.has(pk)) buckets.set(pk, []);
      buckets.get(pk).push(th);
    }
    const out = [];
    for (const pk of PERIOD_ORDER) {
      const arr = buckets.get(pk);
      if (!arr || !arr.length) continue;
      arr.sort((a, b) => new Date(b.latest.date) - new Date(a.latest.date));
      out.push({ period: pk, threads: arr });
    }
    return out;
  }, [list.messages]);

  // While the auth session is still restoring (e.g. right after a page refresh),
  // don't redirect — wait for bootstrap to finish, otherwise refresh kicks to Home.
  if (authLoading) {
    return (
      <DoserneShell title={t.title} wide>
        <div className="flex items-center justify-center py-32" data-testid="email-auth-loading">
          <Loader2 className="w-7 h-7 animate-spin text-slate-300" />
        </div>
      </DoserneShell>
    );
  }
  if (!isAdmin) return <Navigate to="/doserne" replace />;


  const openAttachment = async (att) => {
    try {
      const res = await axios.get(`${API}/doserne/jarvis/email/attachment`, {
        ...authHeader(), params: { folder: active, uid: selected.uid, index: att.index, account_id: activeAccount }, responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: att.content_type || "application/octet-stream" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Couldn't open the attachment"); }
  };

  const addFiles = (files) => {
    Array.from(files || []).forEach((f) => {
      if (f.size > 15 * 1024 * 1024) { toast.error(`${f.name} is too large (max 15MB)`); return; }
      const reader = new FileReader();
      reader.onload = () => setCompose((c) => c ? { ...c, attachments: [...(c.attachments || []), { filename: f.name, content_type: f.type || "application/octet-stream", data: reader.result }] } : c);
      reader.readAsDataURL(f);
    });
  };
  const onPickFiles = (e) => { addFiles(e.target.files); e.target.value = ""; };

  return (
    <DoserneShell title={t.title} wide>
      <div className="w-full">
        {notConnected ? (
          <div className="flex flex-col items-center justify-center text-center gap-4" style={{ height: "calc(100vh - 200px)" }} data-testid="email-notconnected">
            <span className="w-16 h-16 rounded-2xl bg-cyan-100 text-cyan-700 flex items-center justify-center"><Mail className="w-8 h-8" /></span>
            <div>
              <h3 className="text-lg font-black text-slate-800">Connect your email</h3>
              <p className="text-sm text-slate-500 mt-1">Add your mailbox (IMAP/SMTP) to read and send email right here.</p>
            </div>
            <button type="button" onClick={() => setConnectOpen(true)} data-testid="email-connect-cta"
              className="flex items-center gap-2 px-6 py-3 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white font-bold text-sm shadow">
              <Plus className="w-4 h-4" /> Add account
            </button>
          </div>
        ) : (
          <div className="flex flex-col px-2 sm:px-4 py-3 gap-3 overflow-hidden" style={{ height: "calc(100vh - 160px)" }}>
            <WeekCalendar authHeader={authHeader} />
            <div className="flex gap-3 flex-1 overflow-hidden">
            {/* Folders */}
            <aside className="hidden md:flex w-56 flex-shrink-0 flex-col gap-1">
              {/* Account switcher (Outlook-style) */}
              <div className="relative mb-1.5">
                <button type="button" onClick={() => setAcctMenuOpen((v) => !v)} data-testid="email-account-switcher"
                  className="w-full flex items-center gap-2 px-2.5 py-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-left">
                  <span className="w-7 h-7 rounded-full bg-cyan-100 text-cyan-700 flex items-center justify-center flex-shrink-0"><Mail className="w-4 h-4" /></span>
                  <span className="flex-1 truncate text-[12px] font-bold text-slate-800">{accounts.find((a) => a.id === activeAccount)?.email || "…"}</span>
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${acctMenuOpen ? "rotate-180" : ""}`} />
                </button>
                {acctMenuOpen && (
                  <div className="absolute left-0 right-0 top-full mt-1 z-30 bg-white rounded-xl border border-slate-200 shadow-xl overflow-hidden" data-testid="email-account-menu">
                    {accounts.map((a) => (
                      <div key={a.id} className={`flex items-center gap-2 px-2.5 py-2 text-[12px] cursor-pointer ${a.id === activeAccount ? "bg-cyan-50" : "hover:bg-slate-50"}`}
                        onClick={() => switchAccount(a.id)} data-testid={`email-account-${a.id}`}>
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${a.id === activeAccount ? "bg-cyan-500" : "bg-slate-300"}`} />
                        <span className="flex-1 truncate font-semibold text-slate-700">{a.email}</span>
                        {accounts.length > 1 && (
                          <button type="button" onClick={(e) => { e.stopPropagation(); disconnectAccount(a.id); }} data-testid={`email-account-remove-${a.id}`}
                            className="p-1 rounded hover:bg-red-50 text-slate-300 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                        )}
                      </div>
                    ))}
                    <button type="button" onClick={() => { setAcctMenuOpen(false); setConnectOpen(true); }} data-testid="email-add-account"
                      className="w-full flex items-center gap-2 px-2.5 py-2 text-[12px] font-bold text-cyan-700 border-t border-slate-100 hover:bg-cyan-50">
                      <Plus className="w-4 h-4" /> Add account
                    </button>
                  </div>
                )}
              </div>
              <button type="button" onClick={() => openCompose("new")} data-testid="email-compose-btn"
                className="mb-2 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white font-bold text-sm shadow">
                <Plus className="w-4 h-4" /> {t.compose}
              </button>
              {folders.map((f) => {
                const Icon = ROLE_ICON[f.role] || Folder;
                const on = active === f.role || active === f.name;
                return (
                  <button key={f.name} type="button" onClick={() => { setClientsView(false); setActive(f.role !== "custom" ? f.role : f.name); }}
                    data-testid={`email-folder-${f.role}`}
                    className={`flex items-center gap-2 px-2.5 py-2 rounded-lg text-[13px] text-left ${on && !clientsView ? "bg-slate-900 text-white font-bold" : "text-slate-700 hover:bg-slate-100"}`}>
                    <Icon className="w-4 h-4 flex-shrink-0" />
                    <span className="flex-1 truncate">{f.display}</span>
                    {f.unread > 0 && <span className={`text-[9px] font-black px-1 py-0.5 rounded-full ${on && !clientsView ? "bg-cyan-500 text-white" : "bg-cyan-100 text-cyan-700"}`}>{f.unread}</span>}
                  </button>
                );
              })}
              <div className="h-px bg-slate-200 my-2" />
              <button type="button" onClick={() => { setClientsView(true); setSelected(null); }}
                data-testid="email-clients-btn"
                className={`flex items-center gap-2 px-2.5 py-2 rounded-lg text-[13px] text-left ${clientsView ? "bg-slate-900 text-white font-bold" : "text-slate-700 hover:bg-slate-100"}`}>
                <Users className="w-4 h-4 flex-shrink-0" />
                <span className="flex-1 truncate">Clienți</span>
                {clients.length > 0 && <span className={`text-[9px] font-black px-1 py-0.5 rounded-full ${clientsView ? "bg-cyan-500 text-white" : "bg-cyan-100 text-cyan-700"}`}>{clients.length}</span>}
              </button>
            </aside>

            {/* Message list OR Client catalog */}
            {clientsView ? (
              <section className={`${selected ? "hidden lg:flex" : "flex"} flex-col w-full lg:w-[440px] flex-shrink-0 bg-white rounded-2xl border border-slate-200 overflow-hidden`} data-testid="email-clients-panel">
                <div className="p-2.5 border-b border-slate-100 flex items-center gap-2">
                  <div className="flex-1 flex items-center gap-1.5 bg-slate-50 rounded-lg px-2.5 border border-slate-300 focus-within:border-slate-400 transition-colors">
                    <Search className="w-4 h-4 text-slate-400" />
                    <input value={clientSearch} onChange={(e) => setClientSearch(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && loadClients(clientSearch)}
                      placeholder="Caută client după nume sau email…" data-testid="email-clients-search"
                      className="flex-1 bg-transparent py-2 text-sm focus:outline-none" />
                    {clientSearch && <button type="button" onClick={() => { setClientSearch(""); loadClients(""); }}><X className="w-4 h-4 text-slate-400" /></button>}
                  </div>
                  <button type="button" onClick={() => loadClients(clientSearch)} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500" title="Reîncarcă">
                    <RefreshCw className={`w-4 h-4 ${clientsLoading ? "animate-spin" : ""}`} />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto dsc-scroll">
                  {clientsLoading && clients.length === 0 ? (
                    <div className="flex items-center justify-center py-10 text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
                  ) : clients.length === 0 ? (
                    <div className="flex flex-col items-center justify-center text-slate-400 py-16 px-6 text-center">
                      <Users className="w-12 h-12 mb-3 opacity-50" />
                      <div className="text-sm font-bold text-slate-500">{clientSearch ? "Niciun client găsit" : "Catalogul e gol"}</div>
                      <p className="text-xs mt-1">Clienții apar aici automat din proiecte.</p>
                    </div>
                  ) : (
                    <ul className="divide-y divide-slate-100">
                      {clients.map((c) => (
                        <li key={c.key} data-testid={`client-row-${c.key}`}>
                          <button type="button" onClick={() => openClientDetail(c)}
                            className={`w-full px-3 py-2.5 hover:bg-slate-50 flex items-start gap-3 text-left ${selectedClient?._row?.key === c.key ? "bg-cyan-50" : ""}`}>
                            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-cyan-500 to-cyan-700 text-white font-black text-sm flex items-center justify-center flex-shrink-0">
                              {(c.name || c.email || "?").trim().charAt(0).toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-bold text-slate-800 text-sm truncate">{c.name || c.email}</div>
                              {c.email && <div className="text-[11px] text-slate-500 truncate flex items-center gap-1"><Mail className="w-3 h-3" /> {c.email}</div>}
                              {c.phone && <div className="text-[11px] text-slate-500 truncate flex items-center gap-1"><Phone className="w-3 h-3" /> {c.phone}</div>}
                              <div className="text-[10px] text-slate-400 mt-0.5">{c.projects} {c.projects === 1 ? "proiect" : "proiecte"}</div>
                            </div>
                            {c.email && (
                              <span onClick={(e) => { e.stopPropagation(); openComposeForClient(c); }} data-testid={`client-write-${c.key}`}
                                className="flex-shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-[11px] font-bold cursor-pointer">
                                <Plus className="w-3 h-3" /> Scrie
                              </span>
                            )}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </section>
            ) : (
            <section className={`${selected ? "hidden lg:flex" : "flex"} flex-col w-full lg:w-[440px] flex-shrink-0 bg-white rounded-2xl border border-slate-200 overflow-hidden`}>
              <div className="p-2.5 border-b border-slate-100 flex items-center gap-2">
                {/* mobile folder select */}
                <select value={active} onChange={(e) => setActive(e.target.value)} data-testid="email-folder-select"
                  className="md:hidden text-sm rounded-lg border border-slate-200 px-2 py-1.5 bg-white">
                  {folders.map((f) => <option key={f.name} value={f.role !== "custom" ? f.role : f.name}>{f.display}{f.unread ? ` (${f.unread})` : ""}</option>)}
                </select>
                <div className="flex-1 flex items-center gap-1.5 bg-slate-50 rounded-lg px-2.5 border border-slate-300 focus-within:border-slate-400 transition-colors">
                  <Search className="w-4 h-4 text-slate-400" />
                  <input value={search} onChange={(e) => { const v = e.target.value; setSearch(v); if (!v.trim()) loadList(active, 1, ""); }} onKeyDown={(e) => e.key === "Enter" && loadList(active, 1, search)}
                    placeholder={t.search} data-testid="email-search"
                    className="flex-1 bg-transparent py-2 text-sm focus:outline-none" />
                  {search && <button type="button" onClick={() => { setSearch(""); loadList(active, 1, ""); }}><X className="w-4 h-4 text-slate-400" /></button>}
                </div>
                <button type="button" onClick={() => { loadFolders(); loadList(active, 1, search); }} data-testid="email-refresh" className="p-2 rounded-lg hover:bg-slate-100 text-slate-500" title={t.refresh}>
                  <RefreshCw className={`w-4 h-4 ${loadingList ? "animate-spin" : ""}`} />
                </button>
              </div>
              {/* search filters */}
              <div className="flex items-center gap-1.5 px-3 py-1.5 border-b border-slate-100 overflow-x-auto">
                {[["all", t.f_all], ["name", t.f_name], ["files", t.f_files], ["date", t.f_date]].map(([id, label]) => (
                  <button key={id} type="button" data-testid={`email-filter-${id}`}
                    onClick={() => { setScope(id); loadList(active, 1, search, { scope: id }); }}
                    className={`shrink-0 px-3 py-1 rounded-full text-xs font-bold transition-colors ${scope === id ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
                    {label}
                  </button>
                ))}
                <button type="button" data-testid="email-filter-unread"
                  onClick={() => { const nv = !unreadOnly; setUnreadOnly(nv); loadList(active, 1, search, { unreadOnly: nv }); }}
                  className={`shrink-0 inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold transition-colors ${unreadOnly ? "bg-cyan-600 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
                  <MailOpen className="w-3.5 h-3.5" /> {t.f_unread}
                </button>
                {scope === "date" && (
                  <div className="flex items-center gap-1.5 shrink-0 ml-1">
                    <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="email-date-from"
                      className="text-xs border border-slate-200 rounded-lg px-2 py-1 focus:outline-none" />
                    <span className="text-slate-400 text-xs">—</span>
                    <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="email-date-to"
                      className="text-xs border border-slate-200 rounded-lg px-2 py-1 focus:outline-none" />
                    <button type="button" onClick={() => loadList(active, 1, search, { scope: "date" })} data-testid="email-date-apply"
                      className="px-2.5 py-1 rounded-full bg-cyan-600 text-white text-xs font-bold hover:bg-cyan-500">{t.f_apply}</button>
                  </div>
                )}
              </div>
              {/* Sync banner removed per user request (Jun 2026) — initial
                  email downloads happen silently in the background. The
                  list still refreshes automatically as new emails arrive. */}
              <div ref={listScrollRef} className="flex-1 overflow-y-auto dsc-scroll"
                onScroll={(e) => {
                  const el = e.currentTarget;
                  if (!list.has_more || loadingMoreRef.current || loadingList) return;
                  if (el.scrollHeight - el.scrollTop - el.clientHeight < 400) {
                    loadingMoreRef.current = true;
                    Promise.resolve(loadList(active, list.page + 1, search)).finally(() => { loadingMoreRef.current = false; });
                  }
                }}>
                {active === "drafts" && localDrafts.map((d) => (
                  <div key={d.id} className="border-b border-slate-50 flex items-center hover:bg-amber-50/60">
                    <button type="button" onClick={() => openDraftItem(d)} data-testid="email-local-draft" className="flex-1 text-left px-3.5 py-3 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-[9px] font-black px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 flex-shrink-0">DRAFT</span>
                        <span className="flex-1 truncate text-sm font-semibold text-slate-700">{d.to || "(no recipient)"}</span>
                        <span className="text-[11px] text-slate-400 flex-shrink-0">{fmtDate(d.updated_at)}</span>
                      </div>
                      <div className="text-sm truncate mt-0.5 text-slate-500">{d.subject || "(no subject)"}</div>
                    </button>
                    <button type="button" onClick={() => deleteLocalDraft(d)} data-testid="email-local-draft-delete" className="px-3 py-3 text-slate-300 hover:text-red-500" title="Delete draft"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
                {list.messages.length === 0 && !loadingList && (active !== "drafts" || localDrafts.length === 0) && (
                  <div className="text-center text-slate-400 text-sm py-16 px-6">{t.empty}</div>
                )}
                {grouped.map((g) => (
                  <div key={g.period}>
                    <div className="sticky top-0 z-10 bg-slate-100/95 backdrop-blur px-3.5 py-1.5 text-[11px] font-black uppercase tracking-wider text-slate-500 border-b border-slate-200">
                      {t[`p_${g.period}`]}
                    </div>
                    {g.threads.map((th) => {
                      const m = th.latest;
                      const isOpen = !!expanded[th.key];
                      return (
                        <div key={th.key} className="border-b border-slate-50">
                          <button type="button" onClick={() => openMessage(m)} data-testid="email-list-item"
                            className={`w-full text-left px-3.5 py-3 hover:bg-slate-50 ${selected?.uid === m.uid ? "bg-cyan-50" : ""}`}>
                            <div className="flex items-center gap-2">
                              {th.unread && <span className="w-2 h-2 rounded-full bg-cyan-500 flex-shrink-0" />}
                              <span className={`flex-1 truncate text-sm ${th.unread ? "font-black text-slate-900" : "font-medium text-slate-600"}`}>{m.from}</span>
                              <span className="text-[11px] text-slate-400 flex-shrink-0">{fmtDate(m.date)}</span>
                            </div>
                            <div className={`text-sm truncate mt-0.5 ${th.unread ? "text-slate-800 font-semibold" : "text-slate-500"}`}>{m.subject}</div>
                          </button>
                          {th.msgs.length > 1 && (
                            <button type="button" data-testid="email-thread-toggle"
                              onClick={() => setExpanded((e) => ({ ...e, [th.key]: !e[th.key] }))}
                              className="flex items-center gap-1 pl-3.5 pb-2 -mt-1 text-[11px] font-semibold text-cyan-600 hover:text-cyan-700">
                              {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                              <MessagesSquare className="w-3.5 h-3.5" /> {th.msgs.length} {t.msgs}
                            </button>
                          )}
                          {isOpen && th.msgs.length > 1 && th.msgs
                            .filter((x) => x.uid !== m.uid)
                            .sort((a, b) => new Date(b.date) - new Date(a.date))
                            .map((x) => (
                              <button key={x.uid} type="button" onClick={() => openMessage(x)} data-testid="email-thread-item"
                                className={`w-full text-left pl-8 pr-3.5 py-2 border-t border-slate-50 hover:bg-slate-50 ${selected?.uid === x.uid ? "bg-cyan-50" : ""}`}>
                                <div className="flex items-center gap-2">
                                  {x.unread && <span className="w-1.5 h-1.5 rounded-full bg-cyan-500 flex-shrink-0" />}
                                  <span className={`flex-1 truncate text-[13px] ${x.unread ? "font-bold text-slate-800" : "text-slate-500"}`}>{x.from}</span>
                                  <span className="text-[10px] text-slate-400 flex-shrink-0">{fmtDate(x.date)}</span>
                                </div>
                              </button>
                            ))}
                        </div>
                      );
                    })}
                  </div>
                ))}
                {list.has_more && (
                  <div className="w-full py-4 flex items-center justify-center" data-testid="email-loadmore-indicator">
                    <Loader2 className="w-4 h-4 animate-spin text-slate-300" />
                  </div>
                )}
              </div>
            </section>
            )}

            {/* Reading pane */}
            <section className={`${selected || compose || selectedClient ? "flex" : "hidden lg:flex"} flex-col flex-1 bg-white rounded-2xl border border-slate-200 overflow-hidden`}>
              {selectedClient && !selected && !compose ? (
                <div className="flex-1 overflow-y-auto" data-testid="client-detail-pane">
                  {/* Client header */}
                  <div className="px-5 py-4 border-b border-slate-100 bg-gradient-to-br from-slate-50 to-white flex items-start gap-4">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-cyan-500 to-cyan-700 text-white font-black text-xl flex items-center justify-center flex-shrink-0">
                      {(selectedClient.client?.name || selectedClient.client?.email || "?").trim().charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xl font-black text-slate-900 truncate">{selectedClient.client?.name || selectedClient.client?.email || "—"}</div>
                      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-[12px] text-slate-600">
                        {selectedClient.client?.email && <span className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {selectedClient.client.email}</span>}
                        {selectedClient.client?.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {selectedClient.client.phone}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {selectedClient.client?.email && (
                        <button type="button" onClick={() => openComposeForClient({ name: selectedClient.client.name, email: selectedClient.client.email })}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-bold shadow-sm">
                          <Plus className="w-3.5 h-3.5" /> Email nou
                        </button>
                      )}
                      <button type="button" onClick={() => setSelectedClient(null)}
                        className="p-2 rounded-lg hover:bg-slate-100 text-slate-500" title="Închide">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {clientDetailLoading ? (
                    <div className="flex items-center justify-center py-12 text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
                  ) : (
                    <>
                      {/* Projects */}
                      <div className="px-5 py-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Briefcase className="w-4 h-4 text-slate-500" />
                          <div className="text-[11px] font-black uppercase tracking-widest text-slate-500">Proiecte ({(selectedClient.projects || []).length})</div>
                        </div>
                        {(selectedClient.projects || []).length === 0 ? (
                          <div className="text-xs text-slate-400 italic">Niciun proiect pentru acest client.</div>
                        ) : (
                          <div className="grid gap-1.5">
                            {selectedClient.projects.map((p) => {
                              const pt = projectThreads[p.id];
                              const expanded = !!pt;
                              return (
                              <div key={p.id} data-testid={`client-project-${p.id}`}
                                className={`border rounded-xl ${p.is_closed ? "border-slate-200 bg-slate-50" : "border-slate-200 bg-white"} ${expanded ? "ring-1 ring-cyan-200" : ""}`}>
                                <button type="button" onClick={() => toggleProjectThreads(p.id)}
                                  data-testid={`client-project-toggle-${p.id}`}
                                  className={`w-full px-3 py-2 flex items-center gap-3 text-left hover:bg-slate-50 rounded-xl ${p.is_closed ? "text-slate-500" : ""}`}>
                                  <div className={`shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-[10px] font-black ${p.is_closed ? "bg-slate-200 text-slate-500" : "bg-cyan-100 text-cyan-700"}`}>{p.number || "·"}</div>
                                  <div className="flex-1 min-w-0">
                                    <div className="font-bold text-sm truncate">{p.name || "—"}</div>
                                    {p.address && <div className="text-[11px] text-slate-500 truncate">{p.address}</div>}
                                  </div>
                                  {p.is_closed && <span className="text-[9px] font-black uppercase bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded">închis</span>}
                                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${expanded ? "rotate-180" : ""}`} />
                                </button>
                                {expanded && (
                                  <div className="border-t border-slate-100 bg-slate-50/50 px-3 py-2" data-testid={`project-threads-${p.id}`}>
                                    {pt.loading ? (
                                      <div className="flex items-center justify-center py-3 text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /></div>
                                    ) : pt.threads.length === 0 ? (
                                      <div className="text-[11px] text-slate-400 italic py-2 text-center">Nicio conversație pe email despre acest proiect.</div>
                                    ) : (
                                      <ul className="divide-y divide-slate-100 bg-white rounded-lg border border-slate-100 overflow-hidden">
                                        {pt.threads.map((th, i) => (
                                          <li key={i}>
                                            <button type="button"
                                              onClick={(e) => { e.stopPropagation(); setActive(th.last_folder || "inbox"); openMessage({ uid: th.last_uid, folder: th.last_folder, unread: false }); setSelectedClient(null); setClientsView(false); }}
                                              data-testid={`project-thread-${p.id}-${i}`}
                                              className="w-full px-2.5 py-2 hover:bg-slate-50 flex items-start gap-2 text-left">
                                              <MessageSquare className="w-3.5 h-3.5 text-slate-400 mt-0.5 flex-shrink-0" />
                                              <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                  <div className={`text-xs truncate flex-1 ${th.unread > 0 ? "font-black text-slate-900" : "font-bold text-slate-700"}`}>{th.subject}</div>
                                                  {th.count > 1 && <span className="text-[9px] font-black bg-slate-100 text-slate-600 px-1 py-0.5 rounded-full">{th.count}</span>}
                                                  {th.unread > 0 && <span className="text-[9px] font-black bg-cyan-100 text-cyan-700 px-1 py-0.5 rounded-full">{th.unread}</span>}
                                                </div>
                                                <div className="text-[10px] text-slate-500 truncate mt-0.5">{th.preview || "—"}</div>
                                                <div className="text-[9px] text-slate-400 mt-0.5">{th.last_folder}{th.last_date ? ` · ${new Date(th.last_date * 1000).toLocaleDateString()}` : ""}</div>
                                              </div>
                                            </button>
                                          </li>
                                        ))}
                                      </ul>
                                    )}
                                  </div>
                                )}
                              </div>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {/* Email threads */}
                      <div className="px-5 py-4 border-t border-slate-100">
                        <div className="flex items-center gap-2 mb-2">
                          <MessagesSquare className="w-4 h-4 text-slate-500" />
                          <div className="text-[11px] font-black uppercase tracking-widest text-slate-500">Conversații ({(selectedClient.threads || []).length})</div>
                        </div>
                        {(selectedClient.threads || []).length === 0 ? (
                          <div className="text-xs text-slate-400 italic">Nicio conversație pe email cu acest client.</div>
                        ) : (
                          <ul className="divide-y divide-slate-100 border border-slate-100 rounded-xl overflow-hidden">
                            {selectedClient.threads.map((th, i) => (
                              <li key={i} data-testid={`client-thread-${i}`}>
                                <button type="button"
                                  onClick={() => { setActive(th.last_folder || "inbox"); openMessage({ uid: th.last_uid, folder: th.last_folder, unread: false }); setSelectedClient(null); }}
                                  className="w-full px-3 py-2.5 hover:bg-slate-50 flex items-start gap-2 text-left">
                                  <MessageSquare className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <div className={`text-sm truncate flex-1 ${th.unread > 0 ? "font-black text-slate-900" : "font-bold text-slate-700"}`}>{th.subject}</div>
                                      {th.count > 1 && <span className="text-[10px] font-black bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-full">{th.count}</span>}
                                      {th.unread > 0 && <span className="text-[10px] font-black bg-cyan-100 text-cyan-700 px-1.5 py-0.5 rounded-full">{th.unread} necitite</span>}
                                    </div>
                                    <div className="text-[11px] text-slate-500 truncate mt-0.5">{th.preview || "—"}</div>
                                    <div className="text-[10px] text-slate-400 mt-0.5">{th.last_folder}{th.last_date ? ` · ${new Date(th.last_date * 1000).toLocaleDateString()}` : ""}</div>
                                  </div>
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ) : (!selected && !compose ? (
                <div className="flex-1 flex flex-col items-center justify-center text-slate-300">
                  <MailOpen className="w-16 h-16 mb-3" />
                  <p className="text-sm">{t.pick}</p>
                </div>
              ) : (
                <>
                  {/* Inline composer (Outlook-style: above the original message) */}
                  {compose && (
                    <div className="flex-shrink-0 border-b-2 border-slate-200 bg-slate-50/60" data-testid="email-compose-inline">
                      <div className="px-4 py-2.5 flex items-center gap-2 border-b border-slate-100">
                        <button type="button" onClick={sendEmail} disabled={sending || jarvisBusy} data-testid="email-send-btn"
                          className="flex items-center gap-2 px-5 py-2 rounded-xl bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 text-white font-bold text-sm shadow-sm">
                          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {sending ? t.sending : t.send}
                        </button>
                        <button type="button" onClick={() => fileRef.current && fileRef.current.click()} data-testid="email-attach-btn"
                          className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 text-slate-600 font-bold text-sm hover:bg-white" title="Attach files">
                          <Paperclip className="w-4 h-4" />
                        </button>
                        {compose.mode?.startsWith("reply") && (
                          <button type="button" onClick={() => replyWithJarvis(compose.replyMsg || selected)} disabled={jarvisBusy || (!compose.replyMsg && !selected)} data-testid="email-compose-jarvis"
                            className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-cyan-200 text-cyan-700 font-bold text-sm hover:bg-cyan-50">
                            <Sparkles className="w-4 h-4" /> Jarvis
                          </button>
                        )}
                        <button type="button" onClick={saveDraft} disabled={sending || jarvisBusy} data-testid="email-savedraft-btn"
                          className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 text-slate-600 font-bold text-sm hover:bg-white">
                          <FileEdit className="w-4 h-4" /> {t.savedraft}
                        </button>
                        <span className="ml-auto text-xs font-black text-slate-500 flex items-center gap-1.5">
                          {compose.mode?.startsWith("reply") ? <Reply className="w-4 h-4" /> : <Send className="w-4 h-4" />}
                          {compose.mode === "replyAll" ? t.replyall : compose.mode === "reply" ? t.reply : compose.mode === "forward" ? t.forward : t.compose}
                        </span>
                        <button type="button" onClick={() => !sending && closeCompose()} data-testid="email-compose-close" className="p-1.5 rounded-full hover:bg-slate-200 text-slate-500"><X className="w-4 h-4" /></button>
                      </div>
                      {(compose.attachments || []).length > 0 && (
                        <div className="flex flex-wrap gap-2 px-4 py-2 bg-cyan-50/40 border-b border-slate-100" data-testid="email-compose-attachments">
                          {compose.attachments.map((a, i) => (
                            <div key={i} className="flex items-center gap-2 max-w-[220px] pl-1.5 pr-2.5 py-1.5 rounded-lg bg-white border border-cyan-200 shadow-sm">
                              {(a.content_type || "").startsWith("image/") && a.data ? (
                                <img src={a.data} alt={a.filename} className="w-9 h-9 rounded-md object-cover flex-shrink-0 border border-slate-200" data-testid="email-attach-thumb" />
                              ) : (
                                <span className="w-9 h-9 rounded-md bg-cyan-50 flex items-center justify-center flex-shrink-0"><Paperclip className="w-4 h-4 text-cyan-600" /></span>
                              )}
                              <span className="flex-1 truncate text-xs font-semibold text-slate-700">{a.filename}</span>
                              <button type="button" onClick={() => setCompose((c) => ({ ...c, attachments: c.attachments.filter((_, j) => j !== i) }))} data-testid="email-attach-remove">
                                <X className="w-3.5 h-3.5 text-slate-400 hover:text-red-500" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="px-4 py-2 space-y-1.5 max-h-[52vh] overflow-y-auto dsc-scroll">
                        <div className="flex items-center gap-2 border-b border-slate-100 pb-1.5">
                          <span className="text-xs font-bold text-slate-400 w-14">{t.to}</span>
                          <RecipientInput value={compose.to} onChange={(v) => setCompose((c) => (c ? { ...c, to: v } : c))}
                            testId="email-compose-to" placeholder="nume@exemplu.com"
                            authHeader={authHeader} accountId={activeAccount} />
                        </div>
                        <div className="flex items-center gap-2 border-b border-slate-100 pb-1.5">
                          <span className="text-xs font-bold text-slate-400 w-14">{t.cc}</span>
                          <RecipientInput value={compose.cc} onChange={(v) => setCompose((c) => (c ? { ...c, cc: v } : c))}
                            testId="email-compose-cc" placeholder="(optional)"
                            authHeader={authHeader} accountId={activeAccount} />
                        </div>
                        <div className="flex items-center gap-2 border-b border-slate-100 pb-1.5">
                          <span className="text-xs font-bold text-slate-400 w-14">{t.subject}</span>
                          <input value={compose.subject} onChange={(e) => setCompose({ ...compose, subject: e.target.value })} data-testid="email-compose-subject"
                            className="flex-1 text-sm font-semibold bg-transparent focus:outline-none" />
                        </div>
                        <div className="relative pt-1"
                          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                          onDragLeave={() => setDragOver(false)}
                          onDrop={(e) => { e.preventDefault(); setDragOver(false); if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files); }}>
                          <RichEditor ref={editorRef} key={`${compose.mode}-${compose.replyMsg?.uid || "new"}`} initialHTML={compose.body}
                            signatures={signatures}
                            onManageSignatures={() => { setSigModal(true); setSigEdit(null); }}
                            onUploadImage={uploadInlineImage}
                            onChange={(html) => setCompose((c) => (c ? { ...c, body: html } : c))} />
                          {dragOver && (
                            <div className="absolute inset-0 flex items-center justify-center bg-cyan-50/90 border-2 border-dashed border-cyan-400 rounded-lg pointer-events-none z-10" data-testid="email-dropzone">
                              <span className="flex items-center gap-2 text-cyan-700 font-bold text-sm"><Paperclip className="w-4 h-4" /> Drop files here</span>
                            </div>
                          )}
                          {jarvisBusy && (
                            <div className="absolute inset-0 flex items-center justify-center bg-white/70 rounded-lg" data-testid="email-jarvis-busy">
                              <span className="flex items-center gap-2 text-cyan-600 font-bold text-sm"><Loader2 className="w-4 h-4 animate-spin" /> {t.jarvis_busy}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  {selected && (
                  <>
                  <div className="p-4 border-b border-slate-100 flex-shrink-0">
                    <div className="flex items-start gap-2">
                      <button type="button" onClick={() => setSelected(null)} className="lg:hidden p-1.5 -ml-1 rounded hover:bg-slate-100"><ChevronLeft className="w-5 h-5" /></button>
                      <h2 className="flex-1 text-lg font-black text-slate-900 leading-tight" data-testid="email-view-subject">{selected.subject}</h2>
                    </div>
                    <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
                      <div className="text-sm text-slate-500">
                        <span className="font-bold text-slate-700">{selected.from}</span>
                        {selected.from_addr && <span className="text-slate-400"> · {selected.from_addr}</span>}
                      </div>
                      <span className="text-xs text-slate-400">{selected.date && new Date(selected.date).toLocaleString()}</span>
                    </div>
                    {((selected.to || []).length > 0 || (selected.cc || []).length > 0) && (
                      <div className="mt-1.5 space-y-0.5" data-testid="email-view-recipients">
                        {(selected.to || []).length > 0 && (
                          <div className="text-xs text-slate-500 flex gap-1.5">
                            <span className="font-bold text-slate-400 flex-shrink-0">{t.to}:</span>
                            <span className="break-words">{(selected.to || []).map((x) => x.name || x.addr).join(", ")}</span>
                          </div>
                        )}
                        {(selected.cc || []).length > 0 && (
                          <div className="text-xs text-slate-500 flex gap-1.5" data-testid="email-view-cc">
                            <span className="font-bold text-slate-400 flex-shrink-0">{t.cc}:</span>
                            <span className="break-words">{(selected.cc || []).map((x) => x.name || x.addr).join(", ")}</span>
                          </div>
                        )}
                      </div>
                    )}
                    {selected.attachments?.length > 0 && (
                      <div className="mt-3 space-y-3" data-testid="email-attachments">
                        {selected.attachments.some(isImageAtt) && (
                          <div className="flex flex-wrap gap-2">
                            {selected.attachments.filter(isImageAtt).map((att) => (
                              <InlineImage key={att.index} folder={active} uid={selected.uid} att={att}
                                authHeader={authHeader} accountId={activeAccount} onOpen={() => openAttachment(att)} />
                            ))}
                          </div>
                        )}
                        {selected.attachments.some((a) => !isImageAtt(a)) && (
                          <div className="flex flex-wrap gap-2">
                            {selected.attachments.filter((a) => !isImageAtt(a)).map((att) => (
                              <button key={att.index} type="button" onClick={() => openAttachment(att)} data-testid="email-attachment-chip"
                                title={att.filename}
                                className="flex items-center gap-2 max-w-[220px] px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100 text-left">
                                <Paperclip className="w-4 h-4 text-cyan-600 flex-shrink-0" />
                                <span className="flex-1 truncate text-xs font-semibold text-slate-700">{att.filename}</span>
                                <span className="text-[10px] text-slate-400 flex-shrink-0">{att.size ? `${Math.max(1, Math.round(att.size / 1024))}KB` : ""}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                      <button type="button" onClick={() => openCompose("reply", selected)} data-testid="email-reply-btn"
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-900 text-white text-sm font-bold hover:bg-slate-800"><Reply className="w-4 h-4" /> {t.reply}</button>
                      <button type="button" onClick={() => openCompose("replyAll", selected, true)} data-testid="email-replyall-btn"
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 text-sm font-bold hover:bg-slate-100"><ReplyAll className="w-4 h-4" /> {t.replyall}</button>
                      <button type="button" onClick={() => forwardMessage(selected)} data-testid="email-forward-btn"
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 text-sm font-bold hover:bg-slate-100"><Forward className="w-4 h-4" /> {t.forward}</button>
                      <button type="button" onClick={() => replyWithJarvis(selected)} data-testid="email-jarvis-btn"
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-cyan-600 to-blue-600 text-white text-sm font-bold hover:from-cyan-500"><Sparkles className="w-4 h-4" /> {t.jarvis}</button>
                      <button type="button" onClick={() => markUnread(selected)} data-testid="email-markunread-btn"
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 text-sm font-bold hover:bg-slate-50"><Mail className="w-4 h-4" /> {t.markunread}</button>
                      <button type="button" onClick={() => doDelete(selected)} data-testid="email-delete-btn"
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-200 text-red-600 text-sm font-bold hover:bg-red-50"><Trash2 className="w-4 h-4" /> {t.del}</button>
                    </div>
                  </div>
                  <div className="flex-1 overflow-y-auto dsc-scroll">
                    {loadingMsg || selected.loading ? (
                      <div className="flex items-center justify-center h-full"><Loader2 className="w-6 h-6 animate-spin text-slate-300" /></div>
                    ) : selected.html ? (
                      <iframe title="email-body" data-testid="email-body-frame" sandbox="" srcDoc={selected.html}
                        className="w-full h-full border-0 bg-white" />
                    ) : (
                      <pre className="p-4 text-sm text-slate-700 whitespace-pre-wrap font-sans" data-testid="email-body-text">{selected.text || "(empty)"}</pre>
                    )}
                  </div>
                  </>
                  )}
                </>
              ))}
            </section>
            </div>
          </div>
        )}
      </div>

      {/* Hidden file input for inline composer attachments */}
      <input ref={fileRef} type="file" multiple onChange={onPickFiles} style={{ display: "none" }} data-testid="email-file-input" />

      {/* Connect / Add account modal */}
      {connectOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4" onClick={() => !connecting && setConnectOpen(false)}>
          <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="email-connect-modal">
            <div className="flex items-center justify-between px-5 py-3.5 bg-slate-900 text-white">
              <div className="font-black flex items-center gap-2"><Mail className="w-4 h-4" /> Add email account</div>
              <button type="button" onClick={() => !connecting && setConnectOpen(false)} className="p-1.5 rounded-full hover:bg-white/10"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-[11px] font-black uppercase tracking-wide text-slate-400">Email</label>
                <input value={conn.email} onChange={(e) => setConn({ ...conn, email: e.target.value })} data-testid="connect-email" autoFocus
                  placeholder="name@company.com" className="w-full mt-1 text-sm px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 focus:outline-none focus:border-cyan-400" />
              </div>
              <div>
                <label className="text-[11px] font-black uppercase tracking-wide text-slate-400">Password</label>
                <input type="password" value={conn.password} onChange={(e) => setConn({ ...conn, password: e.target.value })} data-testid="connect-password"
                  placeholder="mailbox password" className="w-full mt-1 text-sm px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 focus:outline-none focus:border-cyan-400" />
              </div>
              <div>
                <label className="text-[11px] font-black uppercase tracking-wide text-slate-400">IMAP/SMTP server <span className="font-medium normal-case text-slate-400">(optional — auto-detected)</span></label>
                <input value={conn.host} onChange={(e) => setConn({ ...conn, host: e.target.value })} data-testid="connect-host"
                  placeholder="mail.company.com" className="w-full mt-1 text-sm px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 focus:outline-none focus:border-cyan-400" />
              </div>
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="text-[11px] font-black uppercase tracking-wide text-slate-400">IMAP port</label>
                  <input type="number" value={conn.imap_port} onChange={(e) => setConn({ ...conn, imap_port: e.target.value })} data-testid="connect-imap-port"
                    className="w-full mt-1 text-sm px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 focus:outline-none focus:border-cyan-400" />
                </div>
                <div className="flex-1">
                  <label className="text-[11px] font-black uppercase tracking-wide text-slate-400">SMTP port</label>
                  <input type="number" value={conn.smtp_port} onChange={(e) => setConn({ ...conn, smtp_port: e.target.value })} data-testid="connect-smtp-port"
                    className="w-full mt-1 text-sm px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 focus:outline-none focus:border-cyan-400" />
                </div>
              </div>
            </div>
            <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-end gap-2">
              <button type="button" onClick={() => !connecting && setConnectOpen(false)}
                className="px-4 py-2 rounded-lg border border-slate-200 text-slate-600 font-bold text-sm hover:bg-slate-50">Cancel</button>
              <button type="button" onClick={connectAccount} disabled={connecting} data-testid="connect-submit"
                className="flex items-center gap-2 px-5 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 text-white font-bold text-sm">
                {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Connect
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Signature manager */}
      {sigModal && (
        <div className="fixed inset-0 z-[95] bg-black/40 flex items-center justify-center p-4"
          onClick={() => { setSigModal(false); setSigEdit(null); }}>
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto dsc-scroll shadow-2xl"
            onClick={(e) => e.stopPropagation()} data-testid="signature-modal">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
              <span className="font-black text-slate-800 flex items-center gap-2"><PenLine className="w-4 h-4" /> Signatures</span>
              <button type="button" onClick={() => { setSigModal(false); setSigEdit(null); }} data-testid="signature-close"><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <div className="p-5 space-y-4">
              {/* existing list */}
              <div className="space-y-2">
                {signatures.length === 0 && <div className="text-sm text-slate-400 text-center py-3">No signatures yet.</div>}
                {signatures.map((s) => (
                  <div key={s.id} className="flex items-center gap-2 p-2.5 rounded-lg border border-slate-200" data-testid="signature-row">
                    <span className="flex-1 text-sm font-semibold text-slate-700 truncate">{s.name}{s.is_default && <span className="ml-2 text-[10px] font-bold text-amber-600">★ default</span>}</span>
                    <button type="button" onClick={() => setSigEdit({ ...s })} data-testid="signature-edit" className="px-2 py-1 text-xs font-bold text-cyan-600 hover:bg-cyan-50 rounded">Edit</button>
                    <button type="button" onClick={() => deleteSignature(s.id)} data-testid="signature-delete" className="p-1 text-slate-400 hover:text-red-500"><Trash className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
              {!sigEdit && (
                <button type="button" onClick={() => setSigEdit({ name: "", html: "", is_default: signatures.length === 0 })} data-testid="signature-new"
                  className="w-full py-2.5 rounded-lg border-2 border-dashed border-slate-200 text-sm font-bold text-slate-500 hover:border-cyan-300 hover:text-cyan-600">
                  + New signature
                </button>
              )}
              {/* editor */}
              {sigEdit && (
                <div className="space-y-3 pt-2 border-t border-slate-100">
                  <input value={sigEdit.name} onChange={(e) => setSigEdit((s) => ({ ...s, name: e.target.value }))} data-testid="signature-name"
                    placeholder="Name (e.g. Personal, Sales)" className="w-full text-sm font-semibold px-3 py-2 rounded-lg bg-slate-50 focus:outline-none" />
                  <RichEditor key={sigEdit.id || "new"} initialHTML={sigEdit.html}
                    onUploadImage={uploadInlineImage}
                    onChange={(html) => setSigEdit((s) => ({ ...s, html }))} />
                  <label className="flex items-center gap-2 text-sm text-slate-600">
                    <input type="checkbox" checked={!!sigEdit.is_default} data-testid="signature-default"
                      onChange={(e) => setSigEdit((s) => ({ ...s, is_default: e.target.checked }))} />
                    Auto-add to every reply
                  </label>
                  <div className="flex gap-2">
                    <button type="button" onClick={saveSignature} data-testid="signature-save"
                      className="px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white font-bold text-sm">Save</button>
                    <button type="button" onClick={() => setSigEdit(null)}
                      className="px-4 py-2 rounded-lg border border-slate-200 text-slate-600 font-bold text-sm hover:bg-slate-50">Cancel</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </DoserneShell>
  );
}
